import { useContext, useEffect, useState, Dispatch, SetStateAction } from 'react';
import { PreSaveDataContext } from "pages/MarketingPage/Data/PreSaveProvider";
import { useHistory } from "react-router";
import momentTz from "moment-timezone";
import {
    CampaignPreSaveCreationData,
    BrandContentExistsData,
    PresaveCampaignExistsData,
} from "modules/Types";
import Loader from 'elements/Loader'
import { formatTabsData, getPostCreationValuesFromData } from "../../utils";
import SuccessImage from "assets/images/tick-light.png";
import { track } from "analytics";
import { PreSaveErrorTypes as ErrorTypes } from "modules/Types";
import CopyLinkInputButton from "components/shareable/CopyLinkInputButton";
import { Card } from "./styles";
import { PresaveStep } from "../../PreSave";
import * as Sentry from "@sentry/react";
import { createPresaveCampaign_api } from "api/campaignApi";
import { TabItem } from "elements/TabsSymphony";
import ButtonSymphony from "elements/ButtonSymphony";
import { PlaybookType } from 'modules/Const';
import useBrand, { BrandExistsState } from 'Hooks/useBrand';

const { BRAND_CONTENT_EXISTS, PRE_SAVE_CAMPAIGN_EXISTS } = ErrorTypes;

const DEFAULT_ERROR_MESSAGE = "An error occurred, please reach out to support and we'll help you out as soon as possible.";

type Data =
    | CampaignPreSaveCreationData
    | BrandContentExistsData
    | PresaveCampaignExistsData
    | null;

interface PostCreationProps {
    draft: string | null;
    brandId: number;
    setGoal: Dispatch<SetStateAction<string | null>>;
    setSelectedTab: (tab: TabItem) => void
}

// NOTE: component()
const PostCreation = ({
    draft,
    brandId,
    setGoal,
    setSelectedTab
}: PostCreationProps) => {
    const history = useHistory();
    const { currentBrand } = useBrand() as BrandExistsState;
    const {
        loading,
        setLoading,
        details,
        customize,
        setPostCreationErrorType,
        setShowPostCreation
    } = useContext(PreSaveDataContext);
    const { releaseTitle } = customize;

    const [error, setError] = useState<boolean>(false);
    const [campaignData, setCampaignData] = useState<Data>(null);
    const {
        link,
        id: campaignId,
        errorLabel,
        errorType,
    } = getPostCreationValuesFromData(campaignData);

    const showDefaultError =
        errorType !== PRE_SAVE_CAMPAIGN_EXISTS &&
        errorType !== BRAND_CONTENT_EXISTS &&
        error;
    const showCampaignExistsError = errorType === PRE_SAVE_CAMPAIGN_EXISTS;
    const showBrandContentExistsError = errorType === BRAND_CONTENT_EXISTS;

    const createPresaveCampaign = async () => {
        const commonTrackData = {
            playbookType: PlaybookType.META_PRESAVES,
            brand_id: currentBrand.id,
            brand_name: currentBrand.name,
            brand_slug: currentBrand.slug,
            ...(customize ? customize : {}),
            ...(details ? details : {}),
        }

        track("Submit Campaign", {
            ...commonTrackData,
        });

        try {
            Sentry.setTag('Flow', 'CreatePresaveCampaign')
            Sentry.setContext('Input', { brandId, details, customize, draft })

            momentTz.tz.setDefault(details.timezone.value);

            const presaveDataToSend = formatTabsData(details, customize, draft);

            const presaveData = await createPresaveCampaign_api(brandId, presaveDataToSend);

            if (presaveData) {
                setCampaignData(presaveData as Data);
                setError(false);
                
            } else {
                setError(true);
                track("Pre-Save Creation Error", {
                    ...commonTrackData,
                    error: error,
                    errorLabel,
                });
            }
        } catch (error) {
            Sentry.captureException(error)
            setError(true);
            track("Pre-Save Creation Error", {
                ...commonTrackData,
                error: error,
                errorLabel,
            });
        } finally {
            momentTz.tz.setDefault();
            setLoading(false);
        }
    };

    useEffect(() => {
        createPresaveCampaign();
    }, []);

    const handleOnClickCopy = () => navigator.clipboard.writeText(link);

    const handleClickSeeDetailsButton = () => {
        setPostCreationErrorType(null);
        history.push(`/marketing/campaigns/${campaignId}`);
    };

    const handleGoToMarketingScreen = () => {
        setPostCreationErrorType(null);
        setGoal(null);
        history.push('/marketing');
    };

    const handleRetry = () => {
        setShowPostCreation(false)
        setSelectedTab({ id: PresaveStep.CUSTOMIZE, title: 'Customize' })
    };

    useEffect(() => {
        if (errorType) {
            setPostCreationErrorType(errorType)
        }
    }, [errorType]);

    const renderSeeDetailsButton = () => {
        return (
            <ButtonSymphony
                className="clickSeeDetailsButtonPresave"
                style={{ marginTop: 16, marginBottom: 16 }}
                onClick={handleClickSeeDetailsButton}
                width="100%"
            >
                See Campaign Details
            </ButtonSymphony>
        )
    }

    const renderCopyLinkButton = () => {
        return (
            <CopyLinkInputButton
                link={link}
                onClick={handleOnClickCopy}
            />
        )
    }

    // NOTE: render()
    return (
        <Card>
            {loading &&
                <Loader
                    className="postCreationLoaderPresave"
                    isCentered
                />
            }
            {(error || errorLabel) && !loading &&
                <>
                    <div className="px-28 text-center">
                        <div className="flex flex-col gap-y-3">
                            {showDefaultError &&
                                <>
                                    <span className="font-semibold text-2xl">
                                        {DEFAULT_ERROR_MESSAGE}
                                    </span>
                                    <span className="font-semibold text-xl">{link}</span>
                                </>
                            }
                            {showCampaignExistsError &&
                                <>
                                    <span className="font-semibold text-2xl">
                                        {errorLabel && errorLabel}
                                    </span>
                                    <div className="mt-4">
                                        <span className="font-semibold text-lg">
                                            Share this link with your fans to start collecting
                                            pre-saves:
                                        </span>
                                    </div>
                                    {renderCopyLinkButton()}
                                    {renderSeeDetailsButton()}
                                </>
                            }
                            {showBrandContentExistsError &&
                                <>
                                    <span className="font-semibold text-2xl">
                                        {errorLabel && errorLabel}
                                    </span>
                                    {renderCopyLinkButton()}
                                    <ButtonSymphony
                                        className="goToMarketingButton"
                                        onClick={handleGoToMarketingScreen}
                                        width="100%"
                                        style={{ marginTop: 16, marginBottom: 16 }}
                                    >
                                        Go To Marketing
                                    </ButtonSymphony>
                                </>
                            }
                        </div>
                    </div>
                    {showDefaultError &&
                        <ButtonSymphony
                            className="retryButtonPresave"
                            onClick={handleRetry}
                            width={160}
                        >
                            Retry
                        </ButtonSymphony>
                    }
                </>
            }
            {!error && !errorLabel && !loading &&
                <>
                    <div className="text-center">
                        <img
                            className="somePreSaveImage mx-auto my-4"
                            alt="tick-light.png"
                            src={SuccessImage}
                        />
                        <div className="font-semibold text-2xl">Your pre-save campaign is</div>
                        <div className="px-6 sm:px-36 font-semibold text-2xl break-words">
                            now live for “{releaseTitle}”!
                        </div>
                    </div>
                    <div className="font-semibold text-lg mt-4">
                        Share this link with your fans to start collecting pre-saves:
                    </div>
                    {renderCopyLinkButton()}
                    {renderSeeDetailsButton()}
                </>
            }
        </Card>
    );
};

export default PostCreation;
